import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React from 'react';
import { Chart } from 'react-google-charts';
import { BodySmallRegular, H6Emphasized, XStack, YStack } from 'smoothie';

import type {
    DbosQueueItem,
    DbosQueueTimelineBucket,
} from '../types/DbosTypes';

interface DbosQueueStatusProps {
    queues: DbosQueueItem[];
    timelineBuckets: DbosQueueTimelineBucket[];
}

export const DbosQueueStatus = ({
    queues,
    timelineBuckets,
}: DbosQueueStatusProps) => {
    const hasQueues = queues.length > 0;
    const hasTimeline = timelineBuckets.length > 0;

    // Build chart data for queue throughput
    const chartData = [
        ['Time', 'Enqueued', 'Completed', 'Avg Wait (ms)'],
        ...timelineBuckets.map((bucket) => [
            new Date(bucket.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            }),
            bucket.enqueued,
            bucket.completed,
            bucket.avgWaitMs ?? 0,
        ]),
    ];

    return (
        <YStack gap={12} width="100%">
            <H6Emphasized>Queue Status</H6Emphasized>

            {/* Current Queue Depths */}
            {hasQueues ? (
                <DataTable
                    value={queues}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: '0.875rem' }}
                >
                    <Column
                        field="queueName"
                        header="Queue"
                        style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}
                    />
                    <Column
                        field="enqueued"
                        header="Waiting"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                        body={(row: DbosQueueItem) => (
                            <span
                                style={{
                                    fontWeight:
                                        row.enqueued > 0 ? 'bold' : 'normal',
                                    color:
                                        row.enqueued > 5 ? '#e65100' : '#333',
                                }}
                            >
                                {row.enqueued}
                            </span>
                        )}
                    />
                    <Column
                        field="pending"
                        header="Executing"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                    <Column
                        field="total"
                        header="Total"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right', fontWeight: 'bold' }}
                    />
                </DataTable>
            ) : (
                <XStack
                    padding={12}
                    borderRadius={8}
                    style={{ backgroundColor: '#e8f5e9' }}
                >
                    <BodySmallRegular color="$green1000">
                        All queues idle
                    </BodySmallRegular>
                </XStack>
            )}

            {/* Queue Throughput Chart */}
            {hasTimeline && (
                <YStack gap={4}>
                    <BodySmallRegular color="$gray1000">
                        Queue Throughput Over Time
                    </BodySmallRegular>
                    <Chart
                        chartType="ComboChart"
                        width="100%"
                        height="220px"
                        data={chartData}
                        options={{
                            legend: { position: 'top' },
                            seriesType: 'bars',
                            series: {
                                0: { color: '#1565c0' }, // Enqueued
                                1: { color: '#28a745' }, // Completed
                                2: {
                                    type: 'line',
                                    color: '#e65100',
                                    targetAxisIndex: 1,
                                }, // Avg Wait
                            },
                            vAxes: {
                                0: {
                                    title: 'Count',
                                    minValue: 0,
                                },
                                1: {
                                    title: 'Avg Wait (ms)',
                                    minValue: 0,
                                },
                            },
                            hAxis: {
                                slantedText: true,
                                slantedTextAngle: 45,
                            },
                            chartArea: {
                                width: '75%',
                                height: '60%',
                            },
                            bar: { groupWidth: '70%' },
                        }}
                    />
                </YStack>
            )}
        </YStack>
    );
};
